import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import StyledTable from '../../../components/ui/StyledTable';
import StatusBadge from '../../../components/ui/StatusBadge';


const ClientGridView = ({ clients, selectedClient, onClientSelect, selectedClients, onSelectClient, onBulkSelect }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedClientsLocal, setSelectedClients] = useState(selectedClients || []);
  const itemsPerPage = 10;
  const totalPages = Math.ceil((clients?.length || 0) / itemsPerPage);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedClients = clients?.slice(startIndex, endIndex) || [];

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'bg-error/10 text-error border-error/20',
      medium: 'bg-warning/10 text-warning border-warning/20',
      low: 'bg-success/10 text-success border-success/20'
    };
    return colors?.[priority] || colors?.low;
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-success/10 text-success border-success/20',
      pending: 'bg-warning/10 text-warning border-warning/20',
      inactive: 'bg-muted/10 text-muted-foreground border-border'
    };
    return colors?.[status] || colors?.inactive;
  };

  const handleSelectAll = (e) => {
    if (e?.target?.checked) {
      onBulkSelect(clients?.map(c => c?.id));
    } else {
      onBulkSelect([]);
    }
  };

  const handleSelectClient = (clientId, e) => {
    e?.stopPropagation();
    const newSelection = selectedClients?.includes(clientId)
      ? selectedClients?.filter(id => id !== clientId)
      : [...selectedClients, clientId];
    onBulkSelect(newSelection);
  };

  const columns = [
    {
      header: 'العميل',
      accessor: 'name',
      className: 'w-48',
      render: (row) => (
        <div className="flex items-center gap-2.5">
          <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
            <Image
              src={row?.avatar}
              alt={row?.avatarAlt}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="min-w-0">
            <p className="text-table-cell font-medium text-foreground truncate">{row?.name}</p>
            <p className="text-sm text-muted-foreground font-caption truncate">{row?.clientId}</p>
          </div>
        </div>
      ),
    },
    {
      header: 'رقم الهاتف',
      accessor: 'phone',
      className: 'w-36',
      render: (row) => (
        <div className="flex items-center gap-1.5" dir="ltr">
          <Icon name="Phone" size={15} color="#B8860B" className="flex-shrink-0" />
          <span className="text-table-cell font-data text-foreground whitespace-nowrap">{row?.phone}</span>
        </div>
      ),
    },
    {
      header: 'البريد الإلكتروني',
      accessor: 'email',
      className: 'w-48',
      render: (row) => (
        <div className="flex items-center gap-1.5">
          <Icon name="Mail" size={15} color="#B8860B" className="flex-shrink-0" />
          <span className="text-table-cell text-foreground truncate" dir="ltr">{row?.email}</span>
        </div>
      ),
    },
    {
      header: 'الاهتمامات',
      accessor: 'interests',
      className: 'w-44',
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          {row?.interests?.slice(0, 2)?.map((interest, idx) => (
            <span
              key={idx}
              className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full border border-primary/20"
            >
              {interest}
            </span>
          ))}
          {row?.interests?.length > 2 && (
            <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full">
              +{row?.interests?.length - 2}
            </span>
          )}
        </div>
      ),
    },
    {
      header: 'آخر تواصل',
      accessor: 'lastContact',
      className: 'w-28',
      render: (row) => (
        <span className="text-table-cell text-muted-foreground">{row?.lastContact}</span>
      ),
    },
    {
      header: 'المتابعة',
      accessor: 'followUpDate',
      className: 'w-32',
      render: (row) => (
        <div className="flex items-center gap-1.5">
          <Icon name="Calendar" size={14} color="#B8860B" className="flex-shrink-0" />
          <span className="text-table-cell text-foreground">{row?.followUpDate}</span>
        </div>
      ),
    },
    {
      header: 'الحالة',
      accessor: 'status',
      className: 'w-24',
      render: (row) => {
        const statusMap = {
          active: 'active',
          pending: 'pending',
          completed: 'completed',
        };
        return <StatusBadge status={statusMap?.[row?.status] || 'default'} label={row?.statusLabel} icon="" className="" />;
      },
    },
  ];

  return (
    <div className="space-y-4" dir="rtl">
      {/* Bulk Selection Header */}
      <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            onChange={(e) => {
              if (e?.target?.checked) {
                setSelectedClients(paginatedClients?.map(c => c?.id));
              } else {
                setSelectedClients([]);
              }
            }}
            checked={selectedClients?.length === paginatedClients?.length && paginatedClients?.length > 0}
            className="w-5 h-5 rounded border-border text-primary focus:ring-primary"
          />
          <span className="text-base font-medium text-muted-foreground">
            {selectedClients?.length > 0 ? `تم تحديد ${selectedClients?.length} عميل` : 'تحديد الكل'}
          </span>
        </div>
      </div>

      <StyledTable
        columns={columns}
        data={paginatedClients}
        variant="gold"
        showCheckbox={true}
        selectedRows={selectedClients}
        onSelectRow={(clientId) => {
          if (selectedClients?.includes(clientId)) {
            setSelectedClients(selectedClients?.filter(id => id !== clientId));
          } else {
            setSelectedClients([...selectedClients, clientId]);
          }
        }}
        onSelectAll={(checked) => {
          if (checked) {
            setSelectedClients(paginatedClients?.map(c => c?.id));
          } else {
            setSelectedClients([]);
          }
        }}
        selectedRow={selectedClient}
        onRowClick={(client) => onClientSelect(client?.id)}
        emptyMessage="لا توجد عملاء"
        emptyIcon="Users"
      />

      <div className="flex items-center justify-between p-4 border-t-2 border-border" style={{
        background: 'linear-gradient(135deg, rgba(184, 134, 11, 0.08) 0%, rgba(218, 165, 32, 0.08) 100%)'
      }}>
        <div className="text-base font-medium text-muted-foreground">
          عرض {startIndex + 1}-{Math.min(endIndex, clients?.length)} من إجمالي {clients?.length} عميل
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={handleNextPage}
            disabled={currentPage >= totalPages}
            className="p-3 hover:bg-muted/50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="الصفحة التالية"
          >
            <Icon name="ChevronRight" size={20} color="#B8860B" />
          </button>
          <span className="text-base font-data font-medium text-foreground">{currentPage} / {totalPages}</span>
          <button 
            onClick={handlePreviousPage}
            disabled={currentPage <= 1}
            className="p-3 hover:bg-muted/50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="الصفحة السابقة"
          >
            <Icon name="ChevronLeft" size={20} color="#B8860B" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientGridView;